import { useEffect, useRef, useState } from 'react';
import { apiClient } from '@/lib/api-client';

export type ReservedTreatmentDateState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; date: string }
  | { status: 'forbidden' }
  | { status: 'error' };

const ROME_DATE = new Intl.DateTimeFormat('it-IT', {
  timeZone: 'Europe/Rome',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

/**
 * `YYYY-MM-DD` pool date → `dd/mm/yyyy` in Europe/Rome. The pool date is a calendar day, so it is
 * anchored at UTC noon: Rome is UTC+1/+2, and noon keeps the same calendar day either way.
 */
export function formatRomeDate(isoDate: string): string {
  return ROME_DATE.format(new Date(`${isoDate}T12:00:00Z`));
}

/**
 * Resolves a reserved Treatment Date Pool entry id to its date through
 * `GET /v1/clinical-study/treatment-date-pool` (there is no per-entry read route). `null` id →
 * `'idle'`, no request. A `403` → `'forbidden'`: the role may not read the pool, so the line adds
 * nothing and offers no retry (as `useTreatmentDatePoolPicker` keeps it apart). An id missing from
 * the pool or a failure → `'error'`; `refetch()` retries. Only the newest request writes state, so a
 * slow read for an earlier entry id never overwrites a later one.
 */
export function useReservedTreatmentDate(
  entryId: string | null,
): ReservedTreatmentDateState & { refetch: () => void } {
  // The state is tagged with the entry id it belongs to: a state for another id is never shown.
  const [tagged, setTagged] = useState<{ id: string | null; state: ReservedTreatmentDateState }>({
    id: entryId,
    state: entryId === null ? { status: 'idle' } : { status: 'loading' },
  });

  const latestRequest = useRef(0);

  const load = () => {
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    const setState = (state: ReservedTreatmentDateState) => setTagged({ id: entryId, state });
    if (entryId === null) {
      setState({ status: 'idle' });
      return;
    }
    setState({ status: 'loading' });
    apiClient
      .GET('/v1/clinical-study/treatment-date-pool')
      .then(({ data, error, response }) => {
        if (latestRequest.current !== requestId) {
          return;
        }
        if (response.status === 403) {
          setState({ status: 'forbidden' });
          return;
        }
        const entry =
          error === undefined && response.ok ? data?.find((e) => e.id === entryId) : undefined;
        setState(entry === undefined ? { status: 'error' } : { status: 'ready', date: entry.date });
      })
      .catch(() => {
        if (latestRequest.current === requestId) {
          setState({ status: 'error' });
        }
      });
  };

  // Only the entry id identity should re-trigger the fetch (mirrors SubjectWorkspacePanel).
  useEffect(load, [entryId]);

  // A null id is idle at once, and a new id is loading at once — not one render later when the
  // effect has run: the previous id's date or error must never show for another reservation.
  if (entryId === null) {
    return { status: 'idle', refetch: load };
  }
  if (tagged.id !== entryId) {
    return { status: 'loading', refetch: load };
  }
  return { ...tagged.state, refetch: load };
}
